import { Alert, Box, Button, Chip, Divider, Stack, TextField, Typography } from '@mui/material'
import { AddOutlined, DeleteOutline, VisibilityOutlined } from '@mui/icons-material'
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import LibraryQuestionGroupService from '../../../shared/services/library-question-group/library-question-group.service';
import {
  ILibraryQuestion,
  ILibraryQuestionGroup,
} from '../../../shared/services/library-question-group/library-question-group.model';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../loading-page';
import { GroupItemVisibilityDialog } from '../../dialogs/surveys/group-item-visibility-dialog';
import { SurveyQuestionDialog } from '../../dialogs/surveys/survey-question-dialog';
import { SurveyCalculationsPanel } from '../../presentation-components/surveys/builder/survey-calculations-panel';
import { addPageBreakUpdates, removePageBreakUpdates, sortItemsByPage } from '../../../shared/utils/survey-rules/group-pages';

// Authoring one library survey: its settings, the questions in it, and its emission rules.
//
// The emission-rule and display-condition dialogs are the CLIENT-side ones, pointed at the
// library through an injected api. Everything else they touch (conversion factors, units,
// categories, inventory groups) is global reference data that means the same thing here.
export const PartnerLibrarySurveyDetailPage = () => {
  const libraryService = new LibraryQuestionGroupService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const { id } = useParams()

  const [group, setGroup] = useState<ILibraryQuestionGroup>()
  const [questionBank, setQuestionBank] = useState<ILibraryQuestion[]>([])
  const [questionTypes, setQuestionTypes] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  // The question being authored. `null` means "create a new one".
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState<ILibraryQuestion | null>(null)
  // Set when a question is created from inside this survey, so it is added to the survey as
  // soon as the dialog reports success.
  const [addAfterCreate, setAddAfterCreate] = useState(false)
  // The question-bank ids as they were when the dialog opened. A created question is
  // identified by diffing against this -- UUIDs are not chronologically ordered, so there is
  // no "newest" to sort for.
  const [bankIdsBeforeCreate, setBankIdsBeforeCreate] = useState<Set<string>>(new Set())

  const [visibilityItem, setVisibilityItem] = useState<any>(null)

  const load = () => {
    if (!id) return
    Promise.all([libraryService.getOne(id), libraryService.getAllQuestions(), libraryService.getQuestionTypes()])
      .then(([loaded, bank, types]) => {
        setGroup(loaded)
        setName(loaded.name)
        setDescription(loaded.description || '')
        setQuestionBank(bank)
        setQuestionTypes(types)
      })
      .catch(err => {
        console.log(err)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(load, [id])

  const notifyError = (err: any) => {
    userNotificationsContext.openSnackbar({
      open: true,
      message: err?.response?.data?.message || 'There was an error, please try again',
      backgroundColor: 'text.primary',
    })
    console.log(err)
  }

  const saveSettings = () => {
    if (!id) return
    libraryService.edit(id, { name, description })
      .then(() => { userNotificationsContext.openSnackbar({ open: true, message: 'Saved' }); load() })
      .catch(notifyError)
  }

  // A question created from inside this survey is added to it straight away; one edited from
  // here is not re-added. The partner's bank exists so a question can be REUSED across
  // surveys, which is why creating and adding are separate concerns underneath.
  const questionSaved = () => {
    setQuestionDialogOpen(false)
    const shouldAdd = addAfterCreate
    setAddAfterCreate(false)
    setEditingQuestion(null)
    if (!id || !shouldAdd) { load(); return }
    libraryService.getAllQuestions()
      .then(bank => {
        const created = bank.find(q => !bankIdsBeforeCreate.has(q.id))
        if (!created) { load(); return }
        return libraryService.addItem(id, { libraryQuestionId: created.id, sortOrder: items.length }).then(load)
      })
      .catch(notifyError)
  }

  const addExistingQuestion = (questionId: string) => {
    if (!id) return
    libraryService.addItem(id, { libraryQuestionId: questionId, sortOrder: (group?.libraryQuestionGroupItems?.length || 0) })
      .then(load).catch(notifyError)
  }

  // Page-break maths shared with the client builder via group-pages utils.
  const addPageBreak = (afterItem: any) => {
    if (!id) return
    const updates = addPageBreakUpdates(items, afterItem)
    if (updates.length === 0) return
    libraryService.updateItemPages(id, updates).then(load).catch(notifyError)
  }

  const removePageBreak = (pageNum: number) => {
    if (!id) return
    const updates = removePageBreakUpdates(items, pageNum)
    if (updates.length === 0) return
    libraryService.updateItemPages(id, updates).then(load).catch(notifyError)
  }

  const removeItem = (itemId: string) => {
    if (!id) return
    libraryService.removeItem(id, itemId).then(load).catch(notifyError)
  }

  // Items in the order a respondent meets them: by page, then by position on the page.
  const items = sortItemsByPage(group?.libraryQuestionGroupItems || [])
  const pageNumbers = [...new Set(items.map(i => i.pageNumber ?? 1))].sort((a, b) => a - b)
  if (pageNumbers.length === 0) pageNumbers.push(1)
  const itemsByPage = new Map<number, typeof items>()
  for (const item of items) {
    const page = item.pageNumber ?? 1
    itemsByPage.set(page, [...(itemsByPage.get(page) ?? []), item])
  }
  const allRules = group?.libraryQuestionGroupEmissionRules || []
  const problems = group?.integrityProblems || []

  // The dialogs speak the client-side shape; a library item carries its question under a
  // different association name.
  const groupItemsForDialog = items.map(item => ({
    ...item,
    surveyQuestionId: item.libraryQuestionId,
    surveyQuestion: item.libraryQuestion
      ? { ...item.libraryQuestion, surveyQuestionOptions: item.libraryQuestion.libraryQuestionOptions || [] }
      : undefined,
  })) as any[]

  const alreadyAdded = new Set(items.map(i => i.libraryQuestionId))

  // One question row. Extracted because the page-grouped list renders it inside two maps and
  // an inline copy would be the third place to keep in step.
  const PageItemRow = ({ item }: { item: any }) => (
    <Stack direction='row' alignItems='center' spacing={1} sx={{ py: 0.5 }}>
      <Typography variant='body2' sx={{ flex: 1 }}>{item.libraryQuestion?.prompt}</Typography>
      {(item.libraryQuestion?.libraryQuestionOptions?.length || 0) > 0 &&
        <Chip size='small' variant='outlined' label={`${item.libraryQuestion?.libraryQuestionOptions?.length} options`} />}
      {item.visibilityRule &&
        <Chip size='small' variant='outlined' color='info' label='Conditional' />}
      <Button size='small' onClick={() => {
        setEditingQuestion(item.libraryQuestion || null); setAddAfterCreate(false); setQuestionDialogOpen(true)
      }}>
        Edit
      </Button>
      <Button size='small' startIcon={<VisibilityOutlined fontSize='small' />} onClick={() => setVisibilityItem(item)}>
        Display condition
      </Button>
      <Button size='small' color='inherit' startIcon={<DeleteOutline fontSize='small' />} onClick={() => removeItem(item.id)}>
        Remove
      </Button>
    </Stack>
  )

  return (
    <PageOuterContainer>
      <PageHeading
        includeBackButton={true}
        backButtonLink='/partner-library/surveys'
        backButtonText='Back to library surveys'
        title={group?.name || 'Library survey'}
        hasTabs={false}
      />
      <LoadingPage pageReady={!isLoading}>
        {problems.length > 0 && <Alert severity='warning' sx={{ mb: 2 }}>
          <Typography variant='body2' sx={{ mb: 1 }}>
            This survey can't be pushed until these are fixed:
          </Typography>
          {problems.map((p, i) => <Typography key={i} variant='caption' sx={{ display: 'block' }}>• {p.message}</Typography>)}
        </Alert>}

        {/* ---- settings ---- */}
        <Stack spacing={2} sx={{ mb: 3, maxWidth: 640 }}>
          <TextField label='Name' size='small' value={name} onChange={e => setName(e.target.value)} fullWidth />
          <TextField label='Description' size='small' value={description} onChange={e => setDescription(e.target.value)} fullWidth multiline minRows={2} />
          <Box><Button variant='contained' onClick={saveSettings}>Save</Button></Box>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* ---- questions ---- */}
        <Typography variant='h6' sx={{ mb: 1 }}>Questions</Typography>
        {items.length === 0 &&
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>No questions yet.</Typography>}

        {pageNumbers.map((pageNum, pageIdx) => {
          const pageItems = itemsByPage.get(pageNum) ?? []
          if (pageItems.length === 0) return null
          return (
            <Box key={pageNum} sx={{ mb: 2 }}>
              {pageNumbers.length > 1 && (
                <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
                  <Typography variant='caption' sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                    PAGE {pageNum} OF {pageNumbers.length}
                  </Typography>
                  {/* Removing a break merges this page back into the one before it. */}
                  {pageIdx > 0 && (
                    <Button size='small' variant='text' onClick={() => removePageBreak(pageNum)}
                      sx={{ fontSize: '0.7rem', color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                      remove page break
                    </Button>
                  )}
                </Stack>
              )}
              <Stack spacing={1}>
                {pageItems.map((item, itemIdx) => (
                  <Box key={item.id}>
                    <PageItemRow item={item} />
                    {/* A break can go after any question except the very last one -- there is
                        nothing to push onto a new page. */}
                    {!(pageIdx === pageNumbers.length - 1 && itemIdx === pageItems.length - 1) && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.25 }}>
                        <Button size='small' variant='text' onClick={() => addPageBreak(item)}
                          sx={{ fontSize: '0.7rem', color: 'text.disabled', minWidth: 0, px: 1, '&:hover': { color: 'primary.main' } }}>
                          + page break
                        </Button>
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )
        })}


        <Box sx={{ mb: 2 }}>
          <Button variant='outlined' startIcon={<AddOutlined />}
            onClick={() => {
              setEditingQuestion(null)
              setAddAfterCreate(true)
              setBankIdsBeforeCreate(new Set(questionBank.map(q => q.id)))
              setQuestionDialogOpen(true)
            }}>
            New question
          </Button>
        </Box>

        {questionBank.filter(q => !alreadyAdded.has(q.id)).length > 0 && <Box sx={{ mb: 3 }}>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            Or reuse a question from another of your surveys. Clients get one copy of it, however
            many of your surveys use it:
          </Typography>
          <Stack direction='row' sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {questionBank.filter(q => !alreadyAdded.has(q.id)).map(q => (
              <Chip key={q.id} size='small' label={q.prompt} onClick={() => addExistingQuestion(q.id)} />
            ))}
          </Stack>
        </Box>}

        <Divider sx={{ mb: 2 }} />

        {/* ---- calculations (emission rules + intermediate values) ---- */}
        <Typography variant='h6' sx={{ mb: 1 }}>Calculations</Typography>
        {id && (
          <SurveyCalculationsPanel
            groupId={id}
            groupItems={groupItemsForDialog}
            rules={allRules as any[]}
            onChanged={load}
            api={{
              createEmissionRule: (gid, payload) => libraryService.createRule(gid, payload),
              updateEmissionRule: (gid, rid, payload) => libraryService.editRule(gid, rid, payload),
              deleteEmissionRule: (gid, rid) => libraryService.removeRule(gid, rid),
            }}
          />
        )}
      </LoadingPage>

      {questionDialogOpen &&
        <SurveyQuestionDialog
          open={questionDialogOpen}
          onClose={() => { setQuestionDialogOpen(false); setEditingQuestion(null); setAddAfterCreate(false) }}
          successHandler={questionSaved}
          questionTypes={questionTypes}
          existingQuestion={editingQuestion as any}
          api={{
            createQuestion: (payload) => libraryService.createQuestion(payload),
            updateQuestion: (qid, payload) => libraryService.editQuestion(qid, payload),
            addOption: (qid, payload) => libraryService.addOption(qid, payload),
            deleteOption: (qid, optionId) => libraryService.deleteOption(qid, optionId),
          }}
        />}

      {visibilityItem && id &&
        <GroupItemVisibilityDialog
          open={Boolean(visibilityItem)}
          onClose={() => setVisibilityItem(null)}
          successHandler={() => { setVisibilityItem(null); load() }}
          groupId={id}
          item={{ ...visibilityItem, surveyQuestionId: visibilityItem.libraryQuestionId }}
          // Only the questions BEFORE this one: a display condition can only test an answer
          // the respondent has already given.
          precedingItems={groupItemsForDialog.slice(0, items.findIndex(i => i.id === visibilityItem.id))}
          api={{ updateGroupItem: (gid, itemId, payload) => libraryService.editItem(gid, itemId, payload) }}
        />}
    </PageOuterContainer>
  )
}
